import { useCallback, useRef } from 'react';

/**
 * Returns a callback whose identity never changes, but which always invokes the
 * latest version of `fn`.
 *
 * Use it for handlers passed into memoized subtrees (e.g. the virtualized block
 * list, whose rows compare props by reference): wrapping the handler in
 * `useCallback` with a dependency list would either keep changing identity — and
 * defeat the memo — or, if deps were trimmed to keep it stable, capture stale
 * state. Delegating through a ref avoids both.
 *
 * The ref is refreshed during render, which is safe here because the app runs on
 * React 17 (no concurrent rendering, so a render is never discarded and
 * re-attempted). Only call the returned function from effects or event handlers,
 * never during another component's render.
 */
const useStableCallback = (fn) => {
  const ref = useRef(fn);
  ref.current = fn;

  return useCallback((...args) => ref.current?.(...args), []);
};

export default useStableCallback;
